import React, { useId } from 'react';
import { twMerge } from 'tailwind-merge';

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  label?: string;
  description?: string;
  size?: 'sm' | 'md';
}

const sizeClasses: Record<NonNullable<CheckboxProps['size']>, { input: string; label: string; desc: string }> = {
  sm: { input: '!w-4 !h-4', label: 'text-sm', desc: 'text-xs' },
  md: { input: '!w-[18px] !h-[18px]', label: 'text-sm', desc: 'text-xs' },
};

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, description, size = 'md', className = '', id, ...props }, ref) => {
    const autoId = useId();
    const inputId = id || autoId;
    const sizes = sizeClasses[size];

    if (!label) {
      return (
        <input
          ref={ref}
          type="checkbox"
          id={inputId}
          className={twMerge(
            sizes.input,
            'cursor-pointer rounded',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
          {...props}
        />
      );
    }

    return (
      <label
        htmlFor={inputId}
        className={twMerge(
          'flex items-start gap-2.5 cursor-pointer',
          props.disabled ? 'cursor-not-allowed opacity-60' : '',
        )}
      >
        <input
          ref={ref}
          type="checkbox"
          id={inputId}
          className={twMerge(
            sizes.input,
            'cursor-pointer rounded mt-0.5 shrink-0',
            'disabled:cursor-not-allowed',
            className,
          )}
          {...props}
        />
        <div className="flex flex-col">
          <span className={twMerge(sizes.label, 'font-medium text-text-primary')}>{label}</span>
          {description && (
            <span className={twMerge(sizes.desc, 'text-text-tertiary mt-0.5')}>{description}</span>
          )}
        </div>
      </label>
    );
  }
);

Checkbox.displayName = 'Checkbox';
